'use client';

import {
  ArrowDownLeftIcon,
  ArrowSquareOutIcon,
  ArrowUpRightIcon,
  CheckIcon,
  FileIcon,
  UserIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import type { ResolvedColumn } from '@/shared/mobile-service/lib/columns';
import { Badge } from './ui';

const EM_DASH = '—';

/**
 * Link target for a device-extracted URL. Only ``http:`` and ``https:`` are
 * navigable; ``javascript:``, ``data:``, and other schemes stay plain text.
 */
export function artifactHttpHref(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

function formatDateTime(value: unknown): string {
  if (value == null || value === '') return EM_DASH;
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(value: unknown): string {
  const total = Number(value);
  if (!Number.isFinite(total)) return EM_DASH;
  const s = Math.floor(total % 60);
  const m = Math.floor((total / 60) % 60);
  const h = Math.floor(total / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function formatNumber(value: unknown): string {
  if (value == null || value === '') return EM_DASH;
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : String(value);
}

/** Truncate long cell text; the full value is available via ``title``. */
function truncate(text: string, max = 120): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

function formatFileSize(value: unknown): string {
  // `Number(null)` is 0, which would render a missing size as a confident "0 B".
  if (value == null || value === '') return EM_DASH;
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return EM_DASH;
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  // Bytes are always whole; larger units read better with one decimal.
  const rounded = unit === 0 ? String(size) : size.toFixed(size < 10 ? 1 : 0);
  return `${rounded} ${BYTE_UNITS[unit]}`;
}

/**
 * Group a phone number for legibility without reformatting it.
 *
 * Artifact data carries numbers in whatever shape the device stored them —
 * international, national, short codes, and non-numeric identifiers all arrive
 * in the same column. Imposing a locale format would misrepresent evidence, so
 * this only spaces out an otherwise unpunctuated run of digits.
 */
function formatPhoneNumber(raw: string): string {
  const digits = raw.replace(/[^\d]/g, '');
  if (raw.trim() !== `+${digits}` && raw.trim() !== digits) return raw;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

function formatCoordinate(value: unknown): string {
  // As above: a missing coordinate must not render as a plausible 0.00000.
  if (value == null || value === '') return EM_DASH;
  if (typeof value === 'string' && value.includes(',')) {
    const parts = value.split(',').map((part) => Number(part.trim()));
    if (parts.length === 2 && parts.every(Number.isFinite)) {
      return parts.map((n) => n.toFixed(5)).join(', ');
    }
    return value;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(5) : EM_DASH;
}

/** Direction values vary by vendor; match on a normalized prefix. */
function directionTone(value: string): 'in' | 'out' | 'other' {
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('incoming') || normalized === 'in' || normalized === 'received') {
    return 'in';
  }
  if (normalized.startsWith('outgoing') || normalized === 'out' || normalized === 'sent') {
    return 'out';
  }
  return 'other';
}

/** Candidate fields for a party object's display label, in priority order. */
const PARTY_LABEL_FIELDS = ['name', 'displayName', 'fullName', 'identifier', 'value'];

/** Best-effort human label for one party entry (object with a name-ish field, or a scalar). */
function partyLabel(entry: unknown): string {
  if (entry == null) return '';
  if (typeof entry !== 'object') return String(entry);
  const record = entry as Record<string, unknown>;
  for (const key of PARTY_LABEL_FIELDS) {
    const candidate = record[key];
    if (candidate != null && candidate !== '') return String(candidate);
  }
  return JSON.stringify(entry);
}

/**
 * Media lives at raw S3 URLs with no presign layer, so any given object may be
 * unreachable from the browser. Both image cells degrade to an icon rather than
 * a broken-image glyph, and neither blocks the row from rendering.
 */
function MediaThumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-white/10 text-white/50">
        <FileIcon size={14} />
      </span>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element -- arbitrary S3 hosts; next/image needs configured remotePatterns
    <img
      src={src}
      alt=""
      loading="lazy"
      title={src}
      onError={() => setFailed(true)}
      className="h-8 w-8 rounded object-cover"
    />
  );
}

function Avatar({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white/50">
        <UserIcon size={14} />
      </span>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element -- arbitrary S3 hosts; next/image needs configured remotePatterns
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-7 w-7 rounded-full object-cover"
    />
  );
}

/**
 * Render a single cell for a resolved column. Nested tables and content fields
 * are handled by the DataTable itself (expandable rows / detail pane); this
 * covers the scalar-ish render kinds.
 */
export function CellValue({ col, value }: { col: ResolvedColumn; value: unknown }) {
  switch (col.render) {
    case 'bool': {
      if (value == null) return <span className="text-white/40">{EM_DASH}</span>;
      return value ? (
        <CheckIcon size={16} className="text-emerald-400" />
      ) : (
        <XIcon size={16} className="text-white/50" />
      );
    }
    case 'number':
      return <span className="tabular-nums">{formatNumber(value)}</span>;
    case 'datetime':
      return <span className="whitespace-nowrap tabular-nums">{formatDateTime(value)}</span>;
    case 'duration':
      return <span className="tabular-nums">{formatDuration(value)}</span>;
    case 'badge':
      return value != null && value !== '' ? (
        <Badge>{String(value)}</Badge>
      ) : (
        <span className="text-white/40">{EM_DASH}</span>
      );
    case 'url': {
      if (!value) return <span className="text-white/40">{EM_DASH}</span>;
      const label = String(value);
      const href = artifactHttpHref(label);
      if (!href) return <span title={label}>{truncate(label, 48)}</span>;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-brand-blue-light hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {truncate(label, 48)}
          <ArrowSquareOutIcon size={12} />
        </a>
      );
    }
    case 'list': {
      const arr = Array.isArray(value) ? value : value == null ? [] : [value];
      if (arr.length === 0) return <span className="text-white/40">{EM_DASH}</span>;
      const text = arr.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
      return <span title={text}>{truncate(text)}</span>;
    }
    case 'file_size':
      return <span className="tabular-nums">{formatFileSize(value)}</span>;
    case 'coordinate':
      return <span className="whitespace-nowrap tabular-nums">{formatCoordinate(value)}</span>;
    case 'phone_number': {
      if (value == null || value === '') return <span className="text-white/40">{EM_DASH}</span>;
      const raw = String(value);
      return (
        <a
          href={`tel:${raw.replace(/[^\d+]/g, '')}`}
          className="whitespace-nowrap tabular-nums text-brand-blue-light hover:underline"
          onClick={(e) => e.stopPropagation()}
          title={raw}
        >
          {formatPhoneNumber(raw)}
        </a>
      );
    }
    case 'direction': {
      if (value == null || value === '') return <span className="text-white/40">{EM_DASH}</span>;
      const label = String(value);
      const tone = directionTone(label);
      return (
        <span
          className={`inline-flex items-center gap-1 whitespace-nowrap ${
            tone === 'in' ? 'text-emerald-400' : tone === 'out' ? 'text-brand-blue-light' : 'text-white/70'
          }`}
        >
          {tone === 'in' ? (
            <ArrowDownLeftIcon size={14} weight="bold" />
          ) : tone === 'out' ? (
            <ArrowUpRightIcon size={14} weight="bold" />
          ) : null}
          {label}
        </span>
      );
    }
    case 'media_thumb': {
      if (!value) return <span className="text-white/40">{EM_DASH}</span>;
      const src = String(value);
      return <MediaThumb src={src} />;
    }
    case 'avatar':
      return <Avatar src={value ? String(value) : null} />;
    case 'party_list': {
      const arr = Array.isArray(value) ? value : value == null ? [] : [value];
      const labels = arr.map(partyLabel).filter((label) => label !== '');
      if (labels.length === 0) return <span className="text-white/40">{EM_DASH}</span>;
      const text = labels.join(', ');
      return <span title={text}>{truncate(text)}</span>;
    }
    // Long free-text (e.g. message bodies). The full value lives in the detail
    // pane; the grid shows a truncated single-line preview.
    case 'content':
    default: {
      if (value == null || value === '') return <span className="text-white/40">{EM_DASH}</span>;
      const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return <span title={text}>{truncate(text)}</span>;
    }
  }
}

export {
  formatCoordinate,
  formatDateTime,
  formatDuration,
  formatFileSize,
  formatNumber,
  formatPhoneNumber,
};
